export enum TestDefinitionStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    RETIRED = 'RETIRED',
}

export enum ExpectedOutcome {
    EXPECT_PASS = 'EXPECT_PASS',
    EXPECT_FAIL = 'EXPECT_FAIL',
    EXPECT_SKIP = 'EXPECT_SKIP',
}

// A typed scalar key/value pair. Deliberately NOT a JSON blob: inputs are
// structured so the executor can read a single value without parsing, and so a
// malformed payload is a form error rather than a runtime surprise. A
// textarea-of-JSON here is how the Action seam erodes — do not add one.
export type StepInput = {
    key: string,
    value: string,
}

export type TestStep = {
    // Explicit and 1-based. The array order is the source of truth in the form;
    // stepNumber is renumbered contiguously from 1 on save (see the service),
    // so a reorder or a removal can never leave a gap or a duplicate.
    stepNumber: number,
    // A HANDLE, not a script. Deliberately free text: an unregistered code is
    // legal — the executor fails OPEN and records an `unimplemented` observation.
    // Do not constrain this to a hardcoded list; a UI allow-list would break that
    // seam. Same rule as testing-actions/types.ts.
    actionCode: string,
    inputs?: StepInput[] | null,
    expect: ExpectedOutcome,
}

export type TestDefinition = {
    id: string,
    testCode: string,
    name: string,
    description?: string | null,
    // FOREIGN KEY into console-api's `Application` (FNP-976/979). Optional:
    // `applicationId: ID` is nullable on the API and is NOT validated at write
    // time — a dangling id is legal because an application can be archived
    // after this definition references it.
    //
    // ⚠️ THIS ONE IS LOAD-BEARING: the executor resolves
    // `getApplication(applicationId).url` to pick the base URL for a wake.
    applicationId?: string,
    steps?: TestStep[] | null,
    status: TestDefinitionStatus,
    createdByUsername?: string,
    updatedByUsername?: string,
    created?: string,
    updated?: string,
}

// The shape the Formik form works with. `steps` and each step's `inputs` are
// non-null arrays so the FieldArray editors always have somewhere to write,
// even when the stored record has steps: NULL. Normalised on submit — see
// TestDefinitionsService.
export type TestDefinitionFormValues = Omit<TestDefinition, 'steps'> & {
    steps: TestStep[],
}

export type TestDefinitionRecordFormProps = {
    record: TestDefinitionFormValues,
    // Returns a promise: the drawer AWAITS this and closes only on success,
    // so a rejection must be observable here (FNP-925). Typing it `void`
    // hid that contract and let the drawer close on a failed save.
    handleSubmit: (values: TestDefinitionFormValues) => void | Promise<unknown>,
    handleCancel: () => void,
}

export type ListTestDefinitions = {
    listTestDefinitions: { items: TestDefinition[], nextToken?: string }
}

export type GetTestDefinition = {
    getTestDefinition: TestDefinition
}

export type CreateTestDefinition = {
    createTestDefinition: TestDefinition
}

export type UpdateTestDefinition = {
    updateTestDefinition: TestDefinition
}

export type DeleteTestDefinition = {
    deleteTestDefinition: string
}

export type SetTestDefinitionStatus = {
    setTestDefinitionStatus: TestDefinition
}
